import { observer } from 'mobx-react';
import { FC, useEffect, useMemo, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import { orderProductModalStore } from '../../store/orderProductModalStore';
import { DELIVERY_TYPE } from '~/constants/deliveryTypes';
import { LOCALES } from '~/constants/main';
import { localeStore } from '~/mobx/localesStore';
import client from '~/services/client';
import logger from '~/services/logger';
import { ICashbackInfo } from '~/types/product';
import { formatPrice } from '~/utils/formatter';
import { getTranslate } from '~/utils/helpers';
import { productUtils } from '~/utils/productUtils';
import './style.scss';
import currenciesStore from '~/view/screens/SettingsScreen/store/currenciesStore';

const CashbackInfo: FC = observer((): JSX.Element | null => {
  const [cashback, isLoading] = useCashbackInfo(
    orderProductModalStore.product?.id,
    orderProductModalStore.branch?.id,
    orderProductModalStore.orderType
  );

  const formattedPrice = useMemo(() => {
    const currency = localeStore.language === LOCALES.AR ? currenciesStore.currency?.currency_code_ar : currenciesStore.currency?.currency_code;
    const priceWithCashback = productUtils.calculateProductCashbackAmount(orderProductModalStore.product, cashback);

    return formatPrice(priceWithCashback, currency);
  }, [cashback, orderProductModalStore.product]);

  if (isLoading) {
    return <Skeleton className="cashback-info" style={{ borderRadius: 10 }} />;
  }

  if (cashback?.id == null) {
    return null;
  }

  return (
    <div className="mb-3">
      <div className={`cashback-cart-container`}>
        <div className="price-container cashbackName-price">
          <div className="cashback-name cashback-text-color">
            {cashback?.name ? getTranslate(cashback?.name) : localeStore.t('consumer_promocode_activated_cashback_header')}
          </div>
          <div className="price-container ">
            <img width="16" height="16" src={`${process.env.REACT_APP_CLOUD_FRONT}/media/images/cashback.svg`} />
            <div className="cashback-text-color cashbackPrice">{formattedPrice}</div>
          </div>
        </div>
        <div className="cashback-description cashback-text-color">
          {cashback?.description
            ? `${getTranslate(cashback?.description)}. `
            : `${localeStore.t('consumer_promocode_activated_cashback_description')} `}
        </div>
      </div>
      <>
        <div className="infoTip-divider" />
        <div className="infoTip">{localeStore.t('cashback_final_amount_will_be_in_checkout_stage_infotext')}</div>
      </>
    </div>
  );
});

const useCashbackInfo = (
  productId: number | undefined,
  branchId: number | undefined,
  orderType: DELIVERY_TYPE | undefined
): [ICashbackInfo | null, boolean] => {
  const [cashbackInfo, setCashbackInfo] = useState<ICashbackInfo | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    let isMounted = true;

    const fetchCashbackInfo = async () => {
      if (!productId) return;

      try {
        setIsLoading(true);
        const response = await client.get(`v3/products/${productId}/cashback`, {
          params: { branch_id: branchId, order_type: orderType, time: orderProductModalStore?.time },
        });

        if (isMounted) {
          setCashbackInfo(response);
        }

        setIsLoading(false);
      } catch (error: any) {
        logger.log(error);
      }
    };

    fetchCashbackInfo();

    return () => {
      isMounted = false;
    };
  }, []);

  return [cashbackInfo, isLoading];
};

export default CashbackInfo;
